/**
 * AIMDv2 – Step 1: Program Requirements
 *
 * Collects:
 *  - Program Name (required, text input)
 *  - Program Description (required, min 150 chars, textarea)
 *  - Program Audience (grouped multi-select of the org's Member Attributes)
 *  - Program Tone (multi-select with descriptions, max 3 – static data)
 *
 * Audience options come from the org's member-attribute catalog
 * (`userProfile.data.orgMemberAttributes` in Redux), so they're available
 * immediately during a new creation before any groupId exists. Once a group
 * is available, GET /api/groups/{grpId}/memberAttributes refreshes the catalog
 * and its pre-selected values (grpMemberAttributes) are merged into the wizard
 * state so the dropdown shows the correct initial selection.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import DosenInput from '../../../components/DosenInput';
import AudienceDropdown from '../components/AudienceDropdown';
import ToneDropdown from '../components/ToneDropdown';
import {
  RequirementsData,
  StepValidationResult,
  AudienceOption,
  MemberAttributeGroup,
  ToneOption
} from '../types';
import {
  PROGRAM_DESC_MAX_LENGTH,
  DUMMY_TONE_OPTIONS
} from '../constants';
import { buildAudienceOptionsFromOrgGroups, fetchAudienceOptions, fetchToneOptions } from '../api';
import { useAppSelector } from '../../../../../redux/hooks';
import {
  flattenAudienceRules,
  participatingAudienceRules,
  audienceRulesFromFlat,
  type AudienceMatchType,
  type AudienceRule
} from '../../../utils/memberAttributes';

/** Rebuild the org catalog shape (list → values) from the flat option list, so
 *  a flat pre-selection can be bucketed back into per-list rules. */
function optionsToCatalog(
  options: AudienceOption[]
): Array<{ _id: string; name: string; values: Array<{ _id: string; value: string }> }> {
  const byList = new Map<string, { _id: string; name: string; values: Array<{ _id: string; value: string }> }>();
  for (const opt of options) {
    const category = opt.category || 'Other';
    const categoryId = opt.categoryId || category;
    const list = byList.get(categoryId);
    if (list) list.values.push({ _id: opt.value, value: opt.label });
    else byList.set(categoryId, { _id: categoryId, name: category, values: [{ _id: opt.value, value: opt.label }] });
  }
  return Array.from(byList.values());
}

/** Any/All segmented control for one attribute list. Disabled until the list
 *  has at least one value ticked — a match type with no values has no meaning. */
const WizardMatchToggle: React.FC<{
  value: AudienceMatchType;
  disabled: boolean;
  onChange: (next: AudienceMatchType) => void;
}> = ({ value, disabled, onChange }) => (
  <Box
    sx={{
      display: 'inline-flex',
      border: '1px solid #2A2A2A',
      borderRadius: '4px',
      overflow: 'hidden',
      opacity: disabled ? 0.4 : 1
    }}
  >
    {(['any', 'all'] as AudienceMatchType[]).map((mode) => {
      const active = value === mode;
      return (
        <Box
          key={mode}
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(mode)}
          sx={{
            px: '8px',
            py: '2px',
            border: 'none',
            bgcolor: active ? '#F79420' : 'transparent',
            color: active ? '#FFFFFF' : '#868686',
            fontFamily: 'Inter, sans-serif',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.12s'
          }}
        >
          {mode}
        </Box>
      );
    })}
  </Box>
);

interface RequirementsStepProps {
  data: RequirementsData;
  validation: StepValidationResult;
  showErrors: boolean;
  onChange: (patch: Partial<RequirementsData>) => void;
  groupId: string;
  /** Called once with the raw org attribute groups so the wizard can cache them for the PUT save. */
  onOrgAttributeGroupsLoaded?: (groups: MemberAttributeGroup[]) => void;
  /** Whether the org has any attribute list to pick an audience from, once that is known. */
  onAudienceAvailabilityChange?: (available: boolean) => void;
}

const RequirementsStep: React.FC<RequirementsStepProps> = ({
  data,
  validation,
  showErrors,
  onChange,
  groupId,
  onOrgAttributeGroupsLoaded,
  onAudienceAvailabilityChange
}) => {
  const [audienceOptions, setAudienceOptions] = useState<AudienceOption[]>([]);
  // Loading from the start when there is a group: its catalog fetch decides the lists.
  const [isLoadingAudience, setIsLoadingAudience] = useState(Boolean(groupId));
  // Whether the org's catalog has been read at all (from the profile or the group's fetch).
  const [catalogKnown, setCatalogKnown] = useState(false);
  // Canonical tone list from the BE; the static list stays as fallback.
  const [toneOptions, setToneOptions] = useState<ToneOption[]>(DUMMY_TONE_OPTIONS);
  useEffect(() => {
    let cancelled = false;
    fetchToneOptions()
      .then((tones) => {
        if (!cancelled && tones.length > 0) setToneOptions(tones);
      })
      .catch(() => {
        // fallback list already in state
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const profileOrgMemberAttributes = useAppSelector((s) => s.userProfile.data?.orgMemberAttributes);

  /* Group the flat option list back into its owning attribute lists — the
     per-list UI needs those boundaries, which `options` alone throws away. */
  const audienceLists = React.useMemo(() => {
    const byList = new Map<string, { categoryId: string; category: string; items: AudienceOption[] }>();
    for (const opt of audienceOptions) {
      const category = opt.category || 'Other';
      const categoryId = opt.categoryId || category;
      const existing = byList.get(categoryId);
      if (existing) existing.items.push(opt);
      else byList.set(categoryId, { categoryId, category, items: [opt] });
    }
    return Array.from(byList.values());
  }, [audienceOptions]);

  // The wizard only requires an audience when there is a list to pick it from.
  useEffect(() => {
    if (isLoadingAudience || !catalogKnown) return;
    onAudienceAvailabilityChange?.(audienceLists.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingAudience, catalogKnown, audienceLists.length]);

  const ruleForList = (listId: string, listName: string): AudienceRule =>
    data.audienceRules.find((r) => r.listId === listId) ?? {
      listId,
      listName,
      matchType: 'any',
      valueIds: []
    };

  /* Both fields are written together: `audienceRules` is authoritative and
     `audience` is its flattened mirror, which the AI generation payload and
     the legacy memberAttributes save still read. */
  const commitRules = (rules: AudienceRule[]) => {
    const participating = participatingAudienceRules(rules);
    onChange({ audienceRules: participating, audience: flattenAudienceRules(participating) });
  };

  const setListValues = (listId: string, listName: string, valueIds: string[]) => {
    const others = data.audienceRules.filter((r) => r.listId !== listId);
    const existing = data.audienceRules.find((r) => r.listId === listId);
    commitRules([...others, { listId, listName, matchType: existing?.matchType ?? 'any', valueIds }]);
  };

  const setListMatchType = (listId: string, matchType: AudienceMatchType) => {
    commitRules(data.audienceRules.map((r) => (r.listId === listId ? { ...r, matchType } : r)));
  };

  // Populate the audience options straight from the org's member-attribute
  // catalog (Redux). This is org-level data, so it works during a *new*
  // creation when no groupId exists yet — the API fetch below only layers in
  // this program's pre-selection once a group is available.
  useEffect(() => {
    if (!Array.isArray(profileOrgMemberAttributes)) return;
    const { options, orgAttributeGroups } = buildAudienceOptionsFromOrgGroups(
      profileOrgMemberAttributes as MemberAttributeGroup[]
    );
    setAudienceOptions(options);
    setCatalogKnown(true);
    if (orgAttributeGroups.length > 0) {
      onOrgAttributeGroupsLoaded?.(orgAttributeGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileOrgMemberAttributes]);

  // Fetch audience options from backend (Member Attributes) once a group exists.
  // This refreshes the catalog and applies this program's saved pre-selection.
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    setIsLoadingAudience(true);

    fetchAudienceOptions(groupId, profileOrgMemberAttributes as MemberAttributeGroup[] | undefined)
      .then(({ options, selectedIds, orgAttributeGroups }) => {
        if (cancelled) return;
        setCatalogKnown(true);
        if (options.length > 0) {
          setAudienceOptions(options);
        }
        // Cache raw groups in the wizard for the PUT save payload
        if (orgAttributeGroups.length > 0) {
          onOrgAttributeGroupsLoaded?.(orgAttributeGroups);
        }
        // If the wizard has no audience selection yet, use the backend's
        // pre-selection. It arrives flat, so bucket it into one "any" rule per
        // owning list — the same shape the per-list UI edits.
        if (data.audienceRules.length === 0 && selectedIds.length > 0) {
          const catalog = optionsToCatalog(options.length > 0 ? options : audienceOptions);
          const rules = audienceRulesFromFlat(selectedIds, catalog);
          onChange({ audienceRules: rules, audience: flattenAudienceRules(rules) });
        }
      })
      .catch((err) => {
        console.error('[AIMDv2] Failed to fetch audience options', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAudience(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, profileOrgMemberAttributes]);

  const errors = showErrors ? validation.errors : {};

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        gap: '28px'
      }}
    >
      <Typography
        sx={{
          fontSize: 30,
          fontWeight: 500,
          fontFamily: 'Inter',
          color: 'var(--Text-text-primary, #FDFDFD)'
        }}
      >
        Program Requirements
      </Typography>

      <DosenInput
        label="Program Name*"
        value={data.programName}
        onChange={(e) => onChange({ programName: e.target.value })}
        placeholder="Enter program name"
        error={!!errors.programName}
        helperText={errors.programName}
      />

      <DosenInput
        label="Program Description*"
        value={data.programDescription}
        onChange={(e) => onChange({ programDescription: e.target.value })}
        placeholder="Describe what this program is about..."
        multiline
        rows={3}
        error={!!errors.programDescription}
        helperText={errors.programDescription}
        maxChars={PROGRAM_DESC_MAX_LENGTH}
      />

      {/* Program Audience — one dropdown per org attribute list, each with its
          own Any/All toggle. Matching is AND across lists, `matchType` within
          a list. AudienceDropdown stays a dumb multiselect; match-type lives
          here so the component never has to know about it. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography
          sx={{
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 500
          }}
        >
          Program Audience*
        </Typography>
        {audienceLists.length === 0 && !isLoadingAudience && (
          <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 13 }}>
            No attributes configured for this organization. You&apos;ll choose who joins on the People step.
          </Typography>
        )}
        {audienceLists.map((list) => {
          const rule = ruleForList(list.categoryId, list.category);
          const hasSelection = rule.valueIds.length > 0;
          return (
            <Box key={list.categoryId || list.category} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Typography
                  sx={{
                    color: '#B6B6B6',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 12,
                    fontWeight: 500
                  }}
                >
                  {list.category}
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 11 }}>
                    Person must match
                  </Typography>
                  <WizardMatchToggle
                    value={rule.matchType}
                    disabled={!hasSelection}
                    onChange={(next) => setListMatchType(list.categoryId, next)}
                  />
                </Box>
              </Box>
              <AudienceDropdown
                options={list.items}
                value={rule.valueIds}
                onChange={(val) => setListValues(list.categoryId, list.category, val)}
                placeholder={`Select ${list.category.toLowerCase()}...`}
                loading={isLoadingAudience}
              />
            </Box>
          );
        })}
        {errors.audience && (
          <Typography sx={{ color: '#E5484D', fontFamily: 'Inter, sans-serif', fontSize: 12 }}>
            {errors.audience}
          </Typography>
        )}
      </Box>

      <ToneDropdown
        label="Program tone (up to 3)*"
        options={toneOptions}
        value={data.tone}
        onChange={(val) => onChange({ tone: val })}
        error={errors.tone}
      />
    </Box>
  );
};

export default RequirementsStep;
